import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Página de aterrizaje tras el callback OAuth de Instagram.
 *
 * Existe en dos variantes (la diferencia es el `kind`):
 *   - {@code success} ─ ruta `/instagram-conectado` (frontendSuccessUrl).
 *   - {@code error}   ─ ruta `/instagram-error`     (frontendErrorUrl).
 *
 * <h3>Por qué existe</h3>
 * El flujo OAuth abre una pestaña nueva con la URL de Instagram. Tras
 * autorizar, Instagram nos redirige al backend; el backend a su vez
 * redirige a una de estas rutas. Antes de este componente la pestaña
 * popup se quedaba en la ruta destino sin nadie que la "cerrara", así
 * que el usuario tenía que cerrarla a mano y, peor, la pestaña padre
 * (donde está {@code InstagramSettingsPanel}) no se enteraba de que el
 * OAuth había terminado y mostraba el estado obsoleto hasta refrescar.
 *
 * <h3>Qué hace</h3>
 * <ol>
 *   <li>Detecta si la pestaña actual es popup (es decir, fue abierta
 *       por otra mediante {@code window.open}). Lo confirmamos con
 *       {@code window.opener} no {@code null}.</li>
 *   <li>Si lo es, envía un {@code postMessage} al opener con un payload
 *       JSON describiendo el resultado ({@code success} o {@code error}
 *       más algunos metadatos no sensibles). El {@code targetOrigin}
 *       es el origen actual: nunca {@code *}, para evitar fugar el
 *       mensaje a un opener malicioso de otro origen (defensa contra
 *       ventanas hijas que cambien de origen tras la apertura).</li>
 *   <li>Cierra la pestaña con {@code window.close()}. Si el navegador
 *       lo bloquea (algunos lo prohíben si la pestaña no fue creada por
 *       script — p.ej. el usuario pegó la URL a mano) caemos a un
 *       estado visual con un botón "Ir al dashboard" para que el
 *       usuario tenga forma de salir.</li>
 *   <li>Si NO es popup (acceso directo a la URL), simplemente
 *       muestra el resultado y ofrece un link al dashboard. No
 *       intentamos cerrar porque {@code window.close()} en una pestaña
 *       que no fue abierta por script no funciona.</li>
 * </ol>
 *
 * <h3>Seguridad del postMessage</h3>
 * <ul>
 *   <li>El receptor (en {@code App.js}) valida {@code event.origin ===
 *       window.location.origin} antes de procesar. El sender restringe
 *       {@code targetOrigin} igual. Así un opener de otro origen NO
 *       recibe el mensaje y un opener de nuestro mismo origen pero con
 *       código distinto sí lo procesa con normalidad.</li>
 *   <li>El payload sólo lleva metadatos derivados de la URL que ya
 *       trajo el navegador (botId, code, message): nada que el
 *       backend no haya emitido ya. Si el atacante puede manipular
 *       estos query params, lo único que consigue es hacer que el
 *       {@code SettingsPanel} muestre un mensaje ligeramente distinto
 *       — no escala a tomar control de la sesión.</li>
 *   <li>NO incluimos tokens ni cookies en el postMessage. El backend
 *       nunca pone secretos en la URL del redirect (no hay
 *       {@code access_token} ni similares en los query params).</li>
 * </ul>
 */
function InstagramOAuthCallback({ kind }) {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const obj = {};
    sp.forEach((value, key) => { obj[key] = value; });
    return obj;
  }, [location.search]);

  const isPopup = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      return !!(window.opener && !window.opener.closed && window.opener !== window);
    } catch (_) {
      // Cross-origin opener puede lanzar al leer .closed; si lanza es
      // que existe pero no es nuestro → no lo tratamos como popup
      // controlado.
      return false;
    }
  }, []);

  const [closeBlocked, setCloseBlocked] = useState(false);

  useEffect(() => {
    if (!isPopup) return undefined;

    // Construimos el payload mínimo. Nada de cookies, nada de tokens.
    const payload = kind === 'success'
      ? {
          type: 'instagram-oauth-result',
          status: 'success',
          botId: params.botId ? Number(params.botId) : null,
          botChannelId: params.botChannelId ? Number(params.botChannelId) : null,
          username: params.username || null,
        }
      : {
          type: 'instagram-oauth-result',
          status: 'error',
          code: params.code || 'unknown_error',
          message: params.message || '',
        };

    // postMessage al opener con targetOrigin restringido al nuestro.
    // Si el opener cambió de origen tras abrirnos (caso patológico),
    // el navegador descarta el mensaje silenciosamente — exactamente
    // lo que queremos.
    try {
      window.opener.postMessage(payload, window.location.origin);
    } catch (_) {
      // No-op: si falla, el flujo seguirá funcionando porque el
      // SettingsPanel también recarga el status periódicamente al
      // recuperar foco (visibilitychange listener).
    }

    // Damos un instante para que el opener procese el mensaje antes
    // de cerrar — algunos navegadores cierran tan rápido que el
    // mensaje se pierde si lo hacemos en el mismo tick.
    const closeTimer = setTimeout(() => {
      try {
        window.close();
      } catch (_) {
        setCloseBlocked(true);
      }
      // Si window.close() no hace nada (algunos navegadores lo ignoran
      // silenciosamente sin lanzar), tras 800 ms más mostramos el
      // botón de salida manual.
      setTimeout(() => setCloseBlocked(true), 800);
    }, 150);

    return () => clearTimeout(closeTimer);
  }, [isPopup, kind, params]);

  const isSuccess = kind === 'success';

  return (
    <div
      data-testid={isSuccess ? 'ig-oauth-success' : 'ig-oauth-error'}
      style={containerStyle}
    >
      <div style={cardStyle}>
        <div style={iconStyle}>{isSuccess ? '✓' : '⚠'}</div>
        <h1 style={titleStyle}>
          {isSuccess ? 'Instagram conectado' : 'Conexión cancelada'}
        </h1>
        <p style={subtitleStyle}>
          {isSuccess
            ? (isPopup
                ? 'Cerrando esta ventana y volviendo a la página anterior…'
                : 'La cuenta se ha conectado correctamente. Puedes volver al panel.')
            : (params.message || 'No se ha podido completar la autorización.')}
        </p>
        {(closeBlocked || !isPopup) && (
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            style={buttonStyle}
          >
            Ir al dashboard
          </button>
        )}
      </div>
    </div>
  );
}

const containerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%)',
};
const cardStyle = {
  maxWidth: 420,
  width: '100%',
  padding: '32px 28px',
  background: '#fff',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  textAlign: 'center',
};
const iconStyle = {
  fontSize: 48,
  marginBottom: 12,
  color: '#7c3aed',
};
const titleStyle = {
  margin: '0 0 8px',
  fontSize: 22,
  color: '#1f2937',
};
const subtitleStyle = {
  margin: '0 0 20px',
  fontSize: 14,
  color: '#6b7280',
  lineHeight: 1.5,
};
const buttonStyle = {
  padding: '10px 20px',
  borderRadius: 8,
  border: 'none',
  background: '#7c3aed',
  color: '#fff',
  fontSize: 14,
  cursor: 'pointer',
  fontWeight: 600,
};

export default InstagramOAuthCallback;
